<template>
  <div class="pgm-notice-detail">
    <!-- 로딩 -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
    </div>

    <template v-else-if="notice">
      <!-- 공지사항 내용 -->
      <div class="notice-container">
        <!-- 카테고리 & 제목 -->
        <div class="notice-header-section">
          <div class="category-row">
            <v-chip :color="getCategoryColor(notice.nc_category)" size="small" variant="flat">
              {{ getCategoryText(notice.nc_category) }}
            </v-chip>
            <v-chip v-if="notice.nc_type === 'ADMIN'" color="grey-darken-2" size="small" variant="outlined" class="ml-2">
              전체
            </v-chip>
          </div>
          <h1 class="notice-title">{{ notice.nc_title }}</h1>
          <div class="notice-meta-row">
            <span class="author">{{ notice.nc_type === 'ADMIN' ? '관리자' : notice.nc_sisul_name || '-' }}</span>
            <span class="divider">|</span>
            <span class="date">{{ formatDate(notice.nc_wdate) }}</span>
            <span class="divider">|</span>
            <span class="hit">조회 {{ notice.nc_hit }}</span>
          </div>
        </div>

        <!-- 본문 -->
        <div class="notice-body ql-editor" v-html="notice.nc_content"></div>

        <!-- 첨부파일 -->
        <div v-if="files.length > 0" class="notice-files">
          <div class="files-title">
            <v-icon size="16">mdi-attachment</v-icon>
            첨부파일 {{ files.length }}개
          </div>
          <div class="files-grid">
            <div v-for="file in files" :key="file.nf_idx" class="file-chip" @click="downloadFile(file)">
              <v-icon size="14">{{ getFileIcon(file.nf_type) }}</v-icon>
              <span>{{ file.nf_original_name }}</span>
            </div>
          </div>
        </div>

        <!-- 액션 버튼 (수정 권한이 있을 때만) -->
        <div class="action-row" v-if="canEdit">
          <v-btn variant="outlined" size="small" @click="goToEdit">
            <v-icon start size="small">mdi-pencil</v-icon>수정
          </v-btn>
        </div>
      </div>

      <!-- 공지사항 목록 -->
      <div class="other-notices">
        <div class="section-title">공지사항 목록</div>
        <div class="notice-list">
          <div 
            v-for="item in otherNotices" 
            :key="item.nc_idx" 
            class="notice-list-item"
            :class="{ active: item.nc_idx === notice.nc_idx }"
            @click="goToNotice(item)"
          >
            <v-chip :color="getCategoryColor(item.nc_category)" size="x-small" variant="flat" class="mr-2">
              {{ getCategoryShort(item.nc_category) }}
            </v-chip>
            <span class="item-title">{{ item.nc_title }}</span>
            <span class="item-author">{{ item.nc_type === 'ADMIN' ? '관리자' : item.nc_sisul_name || '-' }}</span>
            <span class="item-date">{{ formatListDate(item.nc_wdate) }}</span>
          </div>
          <div v-if="otherNotices.length === 0" class="empty-list">
            등록된 공지사항이 없습니다.
          </div>
        </div>
      </div>
    </template>

    <!-- 에러 -->
    <div v-else class="error-container">
      <v-icon size="48" color="grey-lighten-1">mdi-alert-circle-outline</v-icon>
      <p>공지사항을 찾을 수 없습니다.</p>
      <v-btn color="primary" variant="tonal" size="small" @click="goBack">목록으로</v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';

interface Notice {
  nc_idx: number;
  nc_type: 'ADMIN' | 'CENTER';
  nc_category: string;
  nc_sisul_code: string | null;
  nc_sisul_name: string | null;
  nc_writer_name: string;
  nc_title: string;
  nc_content: string;
  nc_hit: number;
  nc_wdate: string;
}

interface NoticeFile {
  nf_idx: number;
  nf_original_name: string;
  nf_size: number;
  nf_type: string;
}

const route = useRoute();
const router = useRouter();
const store = useStore();

const loading = ref(true);
const notice = ref<Notice | null>(null);
const files = ref<NoticeFile[]>([]);
const otherNotices = ref<Notice[]>([]);

const user = computed(() => store.state.user);
const canEdit = computed(() => {
  if (!notice.value || !user.value) return false;
  if (user.value.m_role === 'admin') return true;
  if (notice.value.nc_type === 'CENTER' && notice.value.nc_sisul_code === user.value.m_sisul_code) return true;
  return false;
});

const loadNotice = async () => {
  const ncIdx = route.params.ncIdx;
  loading.value = true;
  
  try {
    const [detailRes, listRes] = await Promise.all([
      axios.get(`/api/notice/detail/${ncIdx}`, { withCredentials: true }),
      axios.get('/api/notice/list', { params: { page: 1, limit: 15 }, withCredentials: true })
    ]);
    
    if (detailRes.data.success) {
      notice.value = detailRes.data.notice;
      files.value = detailRes.data.files || [];
    }
    
    if (listRes.data.success) {
      otherNotices.value = listRes.data.notices;
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
  } finally {
    loading.value = false;
  }
};

const downloadFile = (file: NoticeFile) => { window.open(`/api/notice/download/${file.nf_idx}`, '_blank'); };
const goBack = () => { router.push('/pgm/notice'); };
const goToEdit = () => { if (notice.value) router.push(`/pgm/notice/edit/${notice.value.nc_idx}`); };
const goToNotice = (item: Notice | null) => { if (item) router.push(`/pgm/notice/${item.nc_idx}`); };

const formatDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const formatShortDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getMonth()+1}/${d.getDate()}`;
};

const formatListDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
};

const getFileIcon = (type: string) => {
  if (!type) return 'mdi-file';
  if (type.includes('image')) return 'mdi-file-image';
  if (type.includes('pdf')) return 'mdi-file-pdf-box';
  return 'mdi-file';
};

const getCategoryColor = (c: string) => ({ NOTICE: 'primary', UPDATE: 'info', EVENT: 'success', URGENT: 'error' }[c] || 'grey');
const getCategoryText = (c: string) => ({ NOTICE: '공지사항', UPDATE: '업데이트', EVENT: '이벤트', URGENT: '긴급' }[c] || c);
const getCategoryShort = (c: string) => ({ NOTICE: '공지', UPDATE: '업데이트', EVENT: '이벤트', URGENT: '긴급' }[c] || c);

watch(() => route.params.ncIdx, () => { loadNotice(); });
onMounted(() => { loadNotice(); });
</script>

<style lang="scss" scoped>
@import '@vueup/vue-quill/dist/vue-quill.snow.css';

.pgm-notice-detail {
  max-width: 800px;
  margin: 0 auto;
  padding: 16px;
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
  p { margin: 12px 0; font-size: 14px; }
}

.notice-container {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  overflow: hidden;
}

.notice-header-section {
  padding: 16px;
  border-bottom: 1px solid #eee;
  
  .category-row { margin-bottom: 10px; }
  
  .notice-title {
    font-size: 18px;
    font-weight: 700;
    color: #222;
    line-height: 1.4;
    margin: 0 0 10px 0;
  }
  
  .notice-meta-row {
    font-size: 12px;
    color: #888;
    .divider { margin: 0 6px; color: #ddd; }
  }
}

.notice-body {
  padding: 20px 16px;
  min-height: 150px;
  line-height: 1.8;
  font-size: 14px;
  color: #333;
  
  :deep(img) { max-width: 100%; height: auto; border-radius: 4px; }
}

.notice-files {
  padding: 12px 16px;
  background: #f9f9f9;
  border-top: 1px solid #eee;
  
  .files-title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #666;
    margin-bottom: 8px;
  }
  
  .files-grid { display: flex; flex-wrap: wrap; gap: 6px; }
  
  .file-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 16px;
    font-size: 11px;
    color: #333;
    cursor: pointer;
    &:hover { background: #f0f0f0; }
  }
}

.action-row {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
  justify-content: flex-end;
}

.other-notices {
  margin-top: 24px;
  
  .section-title {
    font-size: 14px;
    font-weight: 700;
    color: #333;
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 2px solid #333;
  }
  
  .notice-list-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    &:hover { background: #fafafa; }
    &.active { background: #e3f2fd; }
    
    .item-title {
      flex: 1;
      font-size: 13px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .item-author {
      font-size: 11px;
      color: #666;
      margin-left: 10px;
      white-space: nowrap;
    }
    
    .item-date {
      font-size: 11px;
      color: #999;
      margin-left: 10px;
      white-space: nowrap;
    }
  }
  
  .empty-list {
    padding: 20px;
    text-align: center;
    color: #999;
    font-size: 13px;
  }
}
</style>
